import { AppLayout } from "@/components/layout/app-layout";
import { PageHeader } from "@/components/layout/page-header";
import { ValidationFilterPanel } from "@/components/validation/validation-filter-panel";
import { ValidationGrid } from "@/components/validation/validation-grid";
import { BulkActionsDialog } from "@/components/transactions/bulk-actions-dialog";
import { useState, useMemo } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/hooks/use-toast";

const Validation = () => {
  const { organization } = useAuth();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<"approve" | "reject" | "create-rule" | "mass-edit" | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'review' | 'validated' | null>(null);
  
  const selectedCount = selectedIds.length;

  const organizationName = organization?.name ?? "Swiss Finance OS";

  const periodLabel = useMemo(() => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    return `Q${quarter} ${now.getFullYear()}`;
  }, []);

  const handleActionClick = (action: "approve" | "reject" | "create-rule" | "mass-edit") => {
    if (selectedCount === 0) {
      toast({
        title: "No Selection",
        description: "Please select transactions first.",
        variant: "destructive",
      });
      return;
    }
    setCurrentAction(action);
    setDialogOpen(true);
  };

  const customActions = selectedCount > 0 ? (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-brand-primary mr-2">{selectedCount} selected</span>
      <button 
        onClick={() => handleActionClick("approve")}
        className="px-3 py-2 bg-brand-secondary text-brand-primary text-xs font-medium rounded-lg hover:bg-brand-accent/50 transition-colors"
      >
        <i className="fa-solid fa-check mr-2"></i>Approve All
      </button>
      <button 
        onClick={() => handleActionClick("create-rule")}
        className="px-3 py-2 bg-brand-secondary text-brand-primary text-xs font-medium rounded-lg hover:bg-brand-accent/50 transition-colors"
      >
        <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>Create Rule
      </button>
      <button 
        onClick={() => handleActionClick("reject")}
        className="px-3 py-2 bg-brand-danger-bg text-brand-danger text-xs font-medium rounded-lg hover:bg-brand-danger-bg/80 transition-colors"
      >
        <i className="fa-solid fa-times mr-2"></i>Reject All
      </button>
      <button 
        onClick={() => handleActionClick("mass-edit")}
        className="px-3 py-2 bg-brand-secondary text-brand-primary text-xs font-medium rounded-lg hover:bg-brand-accent/50 transition-colors"
      >
        <i className="fa-solid fa-user-plus mr-2"></i>Assign
      </button>
    </div>
  ) : null;

  return (
    <AppLayout>
      <PageHeader 
        title="Fix Issues"
        subtitle={`Review and resolve validation issues · ${organizationName} · ${periodLabel}`}
        customActions={customActions}
        showActionBar={true}
        showMandateBadge={true}
      />
      <main className="flex-1 p-page-padding lg:p-page-padding-lg overflow-y-auto">
        <ValidationFilterPanel 
          onStatusFilter={setStatusFilter}
        />
        <ValidationGrid 
          statusFilter={statusFilter}
          onSelectionChange={setSelectedIds}
        />
        
        <BulkActionsDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          action={currentAction}
          selectedCount={selectedCount}
          selectedIds={selectedIds}
          onSuccess={() => {
            setSelectedIds([]);
            toast({
              title: "Success",
              description: "Action completed successfully.",
            });
          }}
        />
      </main>
    </AppLayout>
  );
};

export default Validation;